import { queryOptions } from '@tanstack/react-query';
import { mediaHttp } from '@/lib/api/http-client';
import { parseResponse } from '@/lib/api/parse-response';
import type { ConnectorAccount } from '../model/types';
import { MEDIA_CAMPAIGN_QUERY_KEYS } from './query-keys';
import { connectorAccountsSchema } from './schemas';

/**
 * Connected ad accounts a Job can publish into — the real connectors from Knowledge
 * Hub, so an account that was never connected cannot be bound here.
 */
export const connectorAccountsQueryOptions = () =>
  queryOptions({
    queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.connectorAccounts(),
    // Reconnecting happens on another screen, and often in another tab. Coming back
    // to a job still showing "reconnect" on an account that now works reads as the
    // reconnect having failed, so this is one of the few queries worth re-asking for
    // on focus.
    refetchOnWindowFocus: true,
    staleTime: 0,
    queryFn: async ({ signal }) => {
      const res = await mediaHttp.get({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: '/api/v1/media-connectors',
        signal,
      });

      return parseResponse(connectorAccountsSchema, res).items.map(
        (item): ConnectorAccount => ({
          id: item.id,
          platform: item.platform as ConnectorAccount['platform'],
          title: item.title,
          externalAccountId: item.externalAccountId ?? '',
          externalAccountName: item.externalAccountName ?? item.title,
          // Kept as the platform reports it. Collapsing everything that is not
          // `connected` into `expired` meant the screen could only ever say "reconnect",
          // where a revoked account and one that simply errored need different words.
          status: item.status as ConnectorAccount['status'],
        }),
      );
    },
  });
